import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';

import { Box, Button, GridLegacy as Grid, Stack, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DesktopDateTimePicker } from '@mui/x-date-pickers-pro';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useGetActivationEmails } from '~/hooks/apis/sailthru/useActivationEmails';

import { Dayjs } from '~/services/TimeService';

import getColumns from './getColumns';

const EMPTY_FILTERS = {
  lastOccurredAtStart: null,
  lastOccurredAtEnd: null,
  email: '',
  businessName: '',
};

interface FormValues {
  lastOccurredAtStart: Dayjs | null;
  lastOccurredAtEnd: Dayjs | null;
  email: string;
  businessName: string;
}

function EmployeeActivationEmailContainer() {
  const { enqueueSnackbar } = useSnackbar();
  const [pageNum, setPageNum] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchFilters, setSearchFilters] = useState<{
    lastOccurredAtStart: Date | null;
    lastOccurredAtEnd: Date | null;
    email: string;
    businessName: string;
  }>(EMPTY_FILTERS);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: EMPTY_FILTERS,
  });

  const {
    data: queryData,
    isFetching,
    error,
  } = useGetActivationEmails({
    ...searchFilters,
    pageNum,
    pageSize,
  });

  const data = queryData?.result?.data ?? [];
  const totalCount = Number(queryData?.result?.total ?? 0);

  const dataRows = data.map((row) => ({
    id: row.notification_id,
    ...row,
  }));

  useEffect(() => {
    if (error) {
      enqueueSnackbar('Failed to fetch activation emails', { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const onPageChange = useCallback((page: number, newPageSize: number) => {
    setPageNum(page);
    setPageSize(newPageSize);
  }, []);

  const handleSearch = useCallback((formValues: FormValues) => {
    setSearchFilters({
      lastOccurredAtStart: formValues.lastOccurredAtStart?.toDate() ?? null,
      lastOccurredAtEnd: formValues.lastOccurredAtEnd?.toDate() ?? null,
      email: formValues.email.trim(),
      businessName: formValues.businessName.trim(),
    });
    setPageNum(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    reset(EMPTY_FILTERS);
    setSearchFilters(EMPTY_FILTERS);
    setPageNum(0);
  }, [reset]);

  return (
    <>
      <PageSubheader title="Activation Emails" />

      <Grid
        container
        spacing={2}
        justifyContent="flex-start"
        alignItems="center"
        component="form"
        onSubmit={handleSubmit(handleSearch)}
      >
        <Grid item>
          <Controller
            control={control}
            name="lastOccurredAtStart"
            render={({ field }) => {
              return (
                <DesktopDateTimePicker
                  label="From Date (UTC)"
                  disableFuture
                  timezone="UTC"
                  sx={{ width: 300 }}
                  ampm={false}
                  onChange={field.onChange}
                  onAccept={field.onChange}
                  value={field.value}
                  inputRef={field.ref}
                />
              );
            }}
          />
        </Grid>
        <Grid item>
          <Controller
            control={control}
            name="lastOccurredAtEnd"
            render={({ field }) => {
              return (
                <DesktopDateTimePicker
                  label="To Date (UTC)"
                  disableFuture
                  timezone="UTC"
                  sx={{ width: 300 }}
                  ampm={false}
                  onChange={field.onChange}
                  onAccept={field.onChange}
                  value={field.value}
                  inputRef={field.ref}
                />
              );
            }}
          />
        </Grid>

        <Grid item>
          <Controller
            control={control}
            name="email"
            render={({ field }) => <TextField {...field} label="Email" sx={{ width: 300 }} />}
          />
        </Grid>
        <Grid item>
          <Controller
            control={control}
            name="businessName"
            render={({ field }) => <TextField {...field} label="Business Name" sx={{ width: 300 }} />}
          />
        </Grid>

        <Grid item>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" type="submit">
              Search
            </Button>
            <Button variant="text" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isFetching}
          rows={dataRows}
          columns={getColumns()}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: pageNum, pageSize }}
        />
      </Box>
    </>
  );
}

export default EmployeeActivationEmailContainer;
